import { useState, useEffect } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { styles } from './Login.styles'

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908C16.658 14.252 17.64 11.946 17.64 9.2z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  )
}

type View = 'signin' | 'signup' | 'forgot' | 'reset'

// HTML type="email" accepts dot-less hostnames like "ali@elevensignal".
// Require a real domain with a TLD (at least one dot + 2-char suffix).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const isValidEmail = (value: string) => EMAIL_RE.test(value.trim())

export default function Login({ oauthError }: { oauthError?: string | null }) {
  const { signIn, signUp, isRecovery: authIsRecovery } = useAuth()
  const [view, setView] = useState<View>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)

  // Switch to reset view whenever useAuth detects a recovery session.
  // useAuth reads the URL hash synchronously at module load so this fires
  // even after Supabase clears the hash and before PASSWORD_RECOVERY replays.
  useEffect(() => {
    if (authIsRecovery) {
      setView('reset')
      setError('')
      setInfo('')
    }
  }, [authIsRecovery])

  const handleGoogleSignIn = async () => {
    setError('')
    setGoogleLoading(true)
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
          queryParams: { prompt: 'select_account' },
        },
      })
      if (error) throw error
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Google sign-in failed')
      setGoogleLoading(false)
    }
  }

  const handleSignIn = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    setError('')
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.')
      return
    }
    setLoading(true)
    try {
      await signIn(email, password)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Authentication failed')
    } finally {
      setLoading(false)
    }
  }

  const handleSignUp = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    setError('')
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.')
      return
    }
    setLoading(true)
    try {
      await signUp(email, password)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Authentication failed')
    } finally {
      setLoading(false)
    }
  }

  const handleForgot = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    setError('')
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.')
      return
    }
    setLoading(true)
    try {
      const { data: exists, error: rpcError } = await supabase.rpc('check_email_exists', { input_email: email })
      if (rpcError) throw rpcError
      if (!exists) {
        setError('No account found with this email address.')
        return
      }
      const { data: googleOnly, error: googleCheckError } = await supabase.rpc('is_google_only_email', { input_email: email })
      if (googleCheckError) throw googleCheckError
      if (googleOnly) {
        setError('This account signs in with Google. Go back and use "Continue with Google" to sign in — password reset isn\'t available for Google accounts.')
        return
      }
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/login`,
      })
      if (error) throw error
      setInfo(`Reset link sent to ${email}. Check your inbox.`)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email')
    } finally {
      setLoading(false)
    }
  }

  const handleReset = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    setError('')
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    setLoading(true)
    try {
      const { error } = await supabase.auth.updateUser({ password })
      if (error) throw error
      setInfo('Password updated. You can now sign in.')
      setView('signin')
      setPassword('')
      setConfirmPassword('')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update password')
    } finally {
      setLoading(false)
    }
  }

  const switchView = (v: View) => {
    setView(v)
    setError('')
    setInfo('')
  }

  return (
    <div className="auth-page" style={styles.page}>
      <div className="auth-card" style={styles.container}>
        <div className="auth-header" style={styles.header}>
          <div className="auth-logo" style={styles.logoRow}>
            <img
              src="/logo-dark.png"
              alt="Brands Meet Creators"
              style={{ height: '28px', width: 'auto' }}
            />
          </div>
          <p className="auth-title" style={styles.subtitle}>
            {{
              signin: 'Sign in to your account',
              signup: 'Create your account',
              forgot: 'Reset your password',
              reset: 'Set a new password',
            }[view]}
          </p>
        </div>

        {(error || oauthError) && <div style={styles.error}>{error || oauthError}</div>}
        {info  && <div style={styles.infoBox}>{info}</div>}

        {/* ── Sign in ── */}
        {view === 'signin' && (
          <form onSubmit={handleSignIn} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com" required style={styles.input} />
            </div>
            <div style={styles.field}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={styles.label}>Password</label>
              </div>
              <div style={styles.inputWrap}>
                <input type={showPassword ? 'text' : 'password'} value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" required minLength={6} style={{ ...styles.input, paddingRight: '2.5rem' }} />
                <button type="button" onClick={() => setShowPassword(v => !v)} style={styles.eyeBtn}>
                  {showPassword ? <EyeOff size={15} color="var(--text-tertiary)" /> : <Eye size={15} color="var(--text-tertiary)" />}
                </button>
              </div>
            </div>
            <button type="button" onClick={() => switchView('forgot')} style={styles.forgotLink}>
                  Forgot password?
                </button>
            <button type="submit" disabled={loading} style={styles.button}>
              {loading ? 'Loading...' : 'Continue'}
            </button>
          </form>
        )}

        {/* ── Sign up ── */}
        {view === 'signup' && (
          <form onSubmit={handleSignUp} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com" required style={styles.input} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Password</label>
              <div style={styles.inputWrap}>
                <input type={showPassword ? 'text' : 'password'} value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" required minLength={6} style={{ ...styles.input, paddingRight: '2.5rem' }} />
                <button type="button" onClick={() => setShowPassword(v => !v)} style={styles.eyeBtn}>
                  {showPassword ? <EyeOff size={15} color="var(--text-tertiary)" /> : <Eye size={15} color="var(--text-tertiary)" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} style={styles.button}>
              {loading ? 'Loading...' : 'Create account'}
            </button>
          </form>
        )}

        {/* ── Forgot password ── */}
        {view === 'forgot' && (
          <form onSubmit={handleForgot} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com" required style={styles.input} />
            </div>
            <button type="submit" disabled={loading} style={styles.button}>
              {loading ? 'Sending...' : 'Send reset link'}
            </button>
            <button type="button" onClick={() => switchView('signin')} style={styles.backLink}>
              Back to sign in
            </button>
          </form>
        )}

        {/* ── Reset password (after clicking email link) ── */}
        {view === 'reset' && (
          <form onSubmit={handleReset} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>New password</label>
              <div style={styles.inputWrap}>
                <input type={showPassword ? 'text' : 'password'} value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" required minLength={6} style={{ ...styles.input, paddingRight: '2.5rem' }} />
                <button type="button" onClick={() => setShowPassword(v => !v)} style={styles.eyeBtn}>
                  {showPassword ? <EyeOff size={15} color="var(--text-tertiary)" /> : <Eye size={15} color="var(--text-tertiary)" />}
                </button>
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Confirm new password</label>
              <div style={styles.inputWrap}>
                <input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="••••••••" required minLength={6} style={{ ...styles.input, paddingRight: '2.5rem' }} />
                <button type="button" onClick={() => setShowConfirmPassword(v => !v)} style={styles.eyeBtn}>
                  {showConfirmPassword ? <EyeOff size={15} color="var(--text-tertiary)" /> : <Eye size={15} color="var(--text-tertiary)" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} style={styles.button}>
              {loading ? 'Updating...' : 'Update password'}
            </button>
          </form>
        )}

        {(view === 'signin' || view === 'signup') && (
          <>
            <div style={styles.divider}>
              <span style={styles.dividerLine} />
              <span style={styles.dividerText}>or</span>
              <span style={styles.dividerLine} />
            </div>
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={googleLoading}
              style={styles.googleButton}
            >
              <GoogleIcon />
              {googleLoading ? 'Redirecting...' : 'Continue with Google'}
            </button>
            <button
              type="button"
              onClick={() => switchView(view === 'signin' ? 'signup' : 'signin')}
              style={styles.altButton}
            >
              {view === 'signin' ? 'Create a new account' : 'Sign in to existing account'}
            </button>
          </>
        )}

      </div>
    </div>
  )
}
